import { motion } from "framer-motion";
import { FlaskConical, Compass, Users, Handshake, Microscope, GraduationCap, Linkedin } from "lucide-react";
import { PageHero, CTASection, Stat } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { IMG, FOUNDER, FOUNDER_INFO, FOUNDER_VIDEO } from "@/data/content";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";


const CHAPTERS = [
  {
    n: "01",
    title: "It began in a research lab",
    body: "THECREATOR Education was not designed in a boardroom — it was born from the daily rhythm of scientific research: question, investigate, verify, apply. We asked a simple question: what if every classroom worked the way a real laboratory does?",
  },
  {
    n: "02",
    title: "Built by a research scientist",
    body: "The entire PBL-TDLE framework — several thousand experiments, several hundred globally standardized apparatus and 10,000+ open-ended problems — was engineered by a practicing research scientist, so that concept mastery always precedes problem solving.",
  },
  {
    n: "03",
    title: "Proven in a real school",
    body: "It is not a theory. The framework is implemented end-to-end at The Creator International School, where cohorts consistently reach outstanding outcomes across boards, olympiads and world-university admissions.",
  },
  {
    n: "04",
    title: "Scaling to every classroom",
    body: "Our mission now is to bring the same research-lab discipline to K–12 schools everywhere — turning ordinary classrooms into Concept Labs and ordinary students into confident problem solvers.",
  },
];

const VALUES = [
  { icon: Microscope, t: "Evidence over memory", d: "Every concept is verified through experiment before it is applied — never simply recited." },
  { icon: Compass, t: "Problem solving as curriculum", d: "If innovation is the goal, problem solving must be the daily language of learning." },
  { icon: Users, t: "Every student, every concept", d: "We believe any engaged student can become a problem solver, regardless of starting ability." },
  { icon: FlaskConical, t: "Research as a habit", d: "Curiosity, rigour and scientific inquiry are practised daily, from the primary years up." },
];

const TEAM = [
  { icon: FlaskConical, role: "Research & Curriculum", d: "Scientists designing experiments, apparatus and the 10,000+ problem bank." },
  { icon: Microscope, role: "Concept Lab Engineering", d: "Turning classrooms into globally standardized scientific Concept Labs." },
  { icon: GraduationCap, role: "Academic Mentorship", d: "NTP mentors guiding research, olympiads and world-university admissions." },
  { icon: Handshake, role: "School Partnerships", d: "Working with principals and trustees to implement PBL-TDLE end-to-end." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        n="11"
        label="About Us"
        lines={["About THECREATOR", "Education"]}
        intro="A K–12 STEM education company on a mission to make problem solving the curriculum — and to nurture the next generation of researchers, innovators and entrepreneurs."
      />

      {/* Mission split */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative aspect-[4/3] overflow-hidden spotlight-clip border border-white/10">
            <motion.img
              src={IMG.campusWide}
              alt="Students learning in a lecture hall"
              className="w-full h-full object-cover"
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            />
          </div>
          <Reveal className="md:col-span-6 md:pl-6">
            <SectionLabel n="◆">Our Mission</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light">
              To turn classrooms into Concept Labs, and students into problem solvers.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mt-6">
              We go beyond chalk-and-talk teaching by transforming K–12 classrooms into advanced scientific Concept Labs, where students investigate and verify every concept through daily experiments — then apply them to real, open-ended problems.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story chapters */}
      {CHAPTERS.map((c, i) => (
        <section key={c.n} className={`border-t border-white/10 ${i % 2 ? "bg-[#070d18]" : ""}`} data-testid={`about-chapter-${c.n}`}>
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <SectionLabel n={c.n}>Our Story</SectionLabel>
              <h3 className="font-display text-2xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">{c.title}</h3>
            </div>
            <Reveal className="md:col-span-7 md:col-start-6">
              <p className="text-white/65 text-lg md:text-xl leading-relaxed max-w-2xl">{c.body}</p>
            </Reveal>
          </div>
        </section>
      ))}

      {/* Founder — the research scientist behind the framework */}
      <section className="border-t border-white/10 bg-cream text-ink" data-testid="about-founder">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal delay={0.05} className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-ink/10 spotlight-clip">
              <img src={FOUNDER} alt={`${FOUNDER_INFO.name} — ${FOUNDER_INFO.title}`} className="w-full h-full object-cover" data-testid="founder-photo" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-7">
            <SectionLabel n="✦" dark>The Research Scientist Behind the Framework</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-ink mt-8 tracking-tight leading-tight font-light" data-testid="founder-name">
              {FOUNDER_INFO.name}
            </h2>
            <p className="font-mono-label text-[11px] tracking-label uppercase text-ink/60 mt-4" data-testid="founder-title">
              {FOUNDER_INFO.title}
            </p>
            <p className="font-mono-label text-[11px] tracking-label uppercase text-[#1B4A7A] mt-1">
              {FOUNDER_INFO.credentials}
            </p>
            <a
              href="https://www.linkedin.com/in/m-a-rehman-273220134"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-linkedin"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full border border-[#1B4A7A]/30 bg-white text-[#1B4A7A] font-mono-label text-[11px] tracking-label uppercase hover:bg-[#1B4A7A] hover:text-white hover:border-[#1B4A7A] transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              Connect on LinkedIn
            </a>
            <div className="mt-8 space-y-5 max-w-2xl" data-testid="founder-bio">
              {FOUNDER_INFO.bio.map((p, i) => (
                <p key={i} className="text-ink/75 text-base md:text-lg leading-relaxed">{p}</p>
              ))}
            </div>
            <blockquote className="font-display text-xl md:text-2xl text-ink leading-snug tracking-tight mt-10 border-l-2 border-[#1B4A7A] pl-6 max-w-2xl font-light">
              “A nation does not become developed by producing more degree holders. It becomes developed by producing more problem solvers, innovators, researchers, and entrepreneurs.”
            </blockquote>

            {/* Founder video — PBL-TDLE explainer by the founder */}
            <div className="mt-12" data-testid="founder-video">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-[#1B4A7A]">Hear from the Founder</div>
              <h3 className="font-display text-xl md:text-2xl text-ink mt-2 tracking-tight leading-snug max-w-2xl">
                {FOUNDER_VIDEO.title}
              </h3>
              <div className="mt-5 relative aspect-video max-w-2xl overflow-hidden rounded-2xl border border-ink/10 bg-black">
                <YouTubeEmbed id={FOUNDER_VIDEO.id} title={FOUNDER_VIDEO.title} />
              </div>
              <p className="font-mono-label text-[11px] tracking-label uppercase text-ink/50 mt-3">
                — {FOUNDER_VIDEO.author}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="◆">What we believe</SectionLabel>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-10 border border-white/10">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              const colors = ["text-c-blue", "text-c-teal", "text-c-amber", "text-c-violet"];
              return (
                <Reveal key={v.t} delay={(i % 4) * 0.08} className="bg-[#050B14] p-8 hover:bg-[#070d18] transition-colors">
                  <Icon className={`w-8 h-8 ${colors[i % colors.length]}`} />
                  <h3 className="font-display text-xl md:text-2xl text-white mt-6 tracking-tight leading-tight">{v.t}</h3>
                  <p className="text-white/55 leading-relaxed mt-3 text-sm">{v.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="◆">The people behind it</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-3xl">
            Scientists, engineers, educators and mentors — working as one lab.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {TEAM.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.role} delay={(i % 4) * 0.08} className="border border-white/10 p-8 hover:border-accent transition-colors group">
                  <div className="w-12 h-12 flex items-center justify-center border border-white/15 group-hover:border-accent transition-colors">
                    <Icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-display text-xl text-white mt-6 tracking-tight">{t.role}</h3>
                  <p className="text-white/55 leading-relaxed mt-3 text-sm">{t.d}</p>
                </Reveal>
              );
            })}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 mt-20 pt-10 border-t border-white/10">
            <Stat value={5000} suffix="+" label="Experiments designed" />
            <Stat value={1500} suffix="+" label="Global-standard apparatus" />
            <Stat value={10000} suffix="+" label="Open-ended problems" />
            <Stat value={100} suffix="%" label="Board outcomes" />
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
